import type { Advertisement, AdvertiserTier } from "../advertisement";
import type { WalletAsset } from "../asset";
import type { AuditLog } from "../audit-log";
import type { Country } from "../country";
import type { Currency } from "../currency";
import type { Exchanger } from "../exchanger";
import type { KycStatus, KycSubmission } from "../kyc";
import type { TradeMessage } from "../message/message";
import type { Notification, NotificationRecipient } from "../notification";
import type { PaymentMethod } from "../payment-method";
import type { Review } from "../review";
import type { Session } from "../session";
import type { Swap } from "../swap";
import type { SupportTicket } from "../ticket";
import type { Trade } from "../trade";
import type { Transaction } from "../transaction";
import type { Wallet } from "../wallet";
import type { WalletAddress } from "../walletAddress";

export const UserRole = {
  USER: "USER",
  SUPER: "SUPER",
  VENDOR: "VENDOR",
  VIP_USER: "VIP_USER",
  COMPLIANCE: "COMPLIANCE",
  SUPPORT: "SUPPORT",
  DISPUTE_MODERATOR: "DISPUTE_MODERATOR",
  ADMIN: "ADMIN",
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserStatus = {
  ACTIVE: "ACTIVE",
  SUSPENDED: "SUSPENDED",
  BANNED: "BANNED",
  DELETED: "DELETED",
} as const;

export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export const AuthType = {
  EMAIL: "EMAIL",
  GOOGLE: "GOOGLE",
  PHONE: "PHONE",
} as const;

export type AuthType = (typeof AuthType)[keyof typeof AuthType];

export const ReviewModerationStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
} as const;

export type ReviewModerationStatus =
  (typeof ReviewModerationStatus)[keyof typeof ReviewModerationStatus];

export const OtpChannel = {
  EMAIL: "EMAIL",
  SMS: "SMS",
  WHATSAPP: "WHATSAPP",
} as const;

export type OtpChannel = (typeof OtpChannel)[keyof typeof OtpChannel];

export const OtpPurpose = {
  PHONE_VERIFICATION: "PHONE_VERIFICATION",
  LOGIN_2FA: "LOGIN_2FA",
  PASSWORD_RESET: "PASSWORD_RESET",
  WALLET_TRANSFER: "WALLET_TRANSFER",
  CHANGE_PAYMENT_METHOD: "CHANGE_PAYMENT_METHOD",
  CHANGE_EMAIL: "CHANGE_EMAIL",
  CHANGE_PHONE: "CHANGE_PHONE",
  ACCOUNT_DELETION: "ACCOUNT_DELETION",
  TERMS_ACCEPTANCE: "TERMS_ACCEPTANCE",
  DEPOSIT_CONFIRMATION: "DEPOSIT_CONFIRMATION",
  HIGH_VALUE_TRANSACTION: "HIGH_VALUE_TRANSACTION",
  ADMIN_ROLE_ASSIGNMENT: "ADMIN_ROLE_ASSIGNMENT",
  ADMIN_PERMISSION_CHANGE: "ADMIN_PERMISSION_CHANGE",
  ADMIN_ACCOUNT_SUSPENSION: "ADMIN_ACCOUNT_SUSPENSION",
  ADMIN_SYSTEM_CONFIG_CHANGE: "ADMIN_SYSTEM_CONFIG_CHANGE",
  ADMIN_ACTION: "ADMIN_ACTION",
} as const;

export type OtpPurpose = (typeof OtpPurpose)[keyof typeof OtpPurpose];

export interface User {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  merchantName?: string;
  fullName?: string;
  username: string;
  email: string;
  phoneNumber: string;
  reputationScore: number;
  completedTrades: number;
  disputeRate: number;
  sellerTrades: number;
  buyerTrades: number;
  role: UserRole;
  status: UserStatus;
  kycStatus: KycStatus;
  kyc: UserRole;
  avatar?: string;
}

export interface UserSetting {
  id: string;
  language: string;
  timezone: string;
  currency: Currency;
  dateFormat: string;
  theme: string;
  emailAlerts: boolean;
  smsAlerts: boolean;
  pushNotifications: boolean;
  newFeatureAlert: boolean;
  twoFAEnabled: boolean;
  loginAlerts: boolean;
  passwordExpiry: number;
  floatingMargin: number;
  autoCancelMinutes: number;
  autoReplyMessage: string;
  currencyId: string;
  securityAlerts: boolean;
  tradeAlerts: boolean;
  priceAlerts: boolean;
  userId: string;
  user?: User;
  createdAt: string;
  updatedAt?: string | null;
}

export interface Otp {
  id: string;
  identifier: string;
  userId?: string | null;
  user?: User | null;
  purpose: OtpPurpose;
  channel: OtpChannel;
  codeHash: string;
  expiresAt: string;
  consumedAt?: string | null;
  attempts: number;
  lastAtemptAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface Device {
  id: string;
  userId: string;
  deviceName: string;
  ip?: string;
  lastUsedAt?: string;
}

export interface UserProfile {
  id: string;
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  accountAgeDays: number | string;
  middleName?: string;
  reputationTier?: string;
  tradeVolume30d?: string | number;
  completedTrades30d?: string | number;
  disputedTrades?: string | number;
  completionRate?: string | number;
  paymentSuccessRate?: string | number;
  averageRating?: string | number;
  securityCodeAttempts?: string | number;
  txnPINAttempts?: string | number;
  securityCodeLockedUntil?: string | number;
  txnPINLockedUntil?: string | number;
  positiveReviewPercent?: string | number;
  negativeReviewPercent?: string | number;
  username: string;
  merchantName: string;
  countryId: string;
  country: Country;
  nationality: Country;
  phoneNumber?: string;
  avatar?: string;
  role: UserRole;
  advertiserTier: AdvertiserTier;
  status: UserStatus;
  authType: UserStatus;
  kycStatus: KycStatus;
  failedLoginAttempts: number;
  loginLockedUntil: number;
  kycLevel: number;
  emailVerified: boolean;
  phoneVerified?: boolean;
  isSecurityCodeVerified?: boolean;
  twoFactorEnabled?: boolean;
  referralCode?: string;
  reputationScore?: number;
  totalReviews?: number;
  completedTrades?: number;
  cancelledTrades?: number;
  disputeRate: number;
  disputesWon: number;
  disputesLost: number;
  averageReleaseTime: number;
  averageResponseTime: number;
  tradeVolume: number;
  repeatCustomers: number;
  referralRewardsEarned: number | string;
  referralRewardsReceived: number | string;
  moderationStatus: ReviewModerationStatus;
  lastLoginIp?: string | null;
  lastSeen?: string | null;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string | null;
  lastLoginDetails?: {
    browser: string;
    date: string;
    device: string;
    ip: string;
    language: string;
    os: string;
    time: string;
    timezone: string;
    location: {
      ip: string | null;
      city: string | null;
      region: string | null;
      country: string | null;
      countryCode: string | null;
      latitude: string | null;
      longitude: string | null;
      timezone: string | null;
      isp: string | null;
    };
  };

  // relations
  setting: UserSetting;
  otps: Otp[];
  notifications: Notification[];
  notificationRecipients: NotificationRecipient[];
  kycSubmissions: KycSubmission[];
  kycReviewedSubmissions: KycSubmission[];
  sessions: Session[];
  devices: Device[];
  advertisements: Advertisement[];
  paymentMethods: PaymentMethod[];
  wallet: Wallet | null;
  walletAssets: WalletAsset[];
  transactions: Transaction[];
  receiverTransactions: Transaction[];
  buyerTrades: Trade[];
  sellerTrades: Trade[];
  tradeMessages: TradeMessage[];
  reviews: Review[];
  exchangers: Exchanger[];
  followedExchangers: Exchanger[];
  swaps: Swap[];
  assets: WalletAsset[];
  auditLogs: AuditLog[];
  reviewsReceived: Review[];
  reviewsWritten: Review[];
  walletAddresses: WalletAddress[];
  tickets: SupportTicket[];
}

export interface UserReputationScore {
  reputationScore: number;
  reputationTier: string;
  tradeVolume: number;
  tradeVolume30d: number;
  completedTrades: number;
  completedTrades30d: number;
  activeTradesCount: number;
  completionRate: number;
  paymentSuccessRate: number;
  averageReleaseTime: number;
  averageResponseTime: number;
  repeatCustomers: number;
  repeatCustomersCount: number;
  disputesOpened: number;
  disputeRate: number;
  totalReviews: number;
  averageRating: number;
  positiveReviewPercent: number;
  negativeReviewPercent: number;
  accountAgeDays: number;
  lastActiveAt: string;
  cancelRate: number;
  tradeReliabilityScore: number;
  tradePerformanceScore: number;
  reviewQualityScore: number;
  disputePenaltyScore: number;
  activityScore: number;
}
